import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryService } from "../services/query-service";
import { QueryKeys } from "../config/query-keys";
import { useToast } from "@/features/toast";
import { useTranslation } from "react-i18next";
import useAuthStore from "@/store/use-auth-store";

/**
 * Hook for fetching agreement by name
 */
export const useAgreement = (name: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: QueryKeys.AGREEMENTS.byName(name),
    queryFn: () => queryService.getAgreement(name),
    enabled: options?.enabled ?? !!name,
    staleTime: 10 * 60 * 1000, // 10 dakika
    gcTime: 15 * 60 * 1000, // 15 dakika
  });
};

/**
 * Hook for confirming agreement
 */
export const useConfirmAgreement = () => {
  const queryClient = useQueryClient();
  const { success, error } = useToast();
  const { t } = useTranslation();
  const { refreshUserData } = useAuthStore();

  return useMutation({
    mutationFn: (agreementName: string) =>
      queryService.confirmAgreement(agreementName),
    onSuccess: () => {
      // Success toast
      success(t("agreements.confirmSuccess"));

      // Invalidate user query to refresh agreementConfirmations
      queryClient.invalidateQueries({
        queryKey: QueryKeys.USER.detail(),
      });

      // Refresh user data in auth store
      refreshUserData(true);
    },
    onError: (confirmError: any) => {
      console.error("❌ Confirm agreement error:", confirmError);
      error(t("agreements.confirmError"));
    },
  });
};
